// src/modules/module_step6.tsx
import React, {
    useState,
    useRef,
    useEffect,
    useCallback
} from 'react';
import config from '../config/config';
import {
    Card,
    CardHeader,
    CardTitle,
    CardSubtitle,
    CardBody
} from '@progress/kendo-react-layout';
import { Input } from '@progress/kendo-react-inputs';
import styled from 'styled-components';
import { Button } from '@progress/kendo-react-buttons';
import SignaturePad from 'react-signature-canvas';
import axios from 'axios';
import { useForm } from '../contexts/FormContext';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import GetUserAvatar from '../components/GetUserAvatar';

const encodePath = (p: string) => p.split('/').map(encodeURIComponent).join('/');

/* ------------------------------------------------------------------ */
/* Styling & Hooks                                                    */
/* ------------------------------------------------------------------ */
const StyledButton = styled(Button)`
    font-weight: bold;
    font-size: 20px;
`;

const useIsMobile = (bp = 680) => {
    const [isM, setIsM] = useState(
        typeof window !== 'undefined' ? window.innerWidth < bp : false
    );
    useEffect(() => {
        const h = () => setIsM(window.innerWidth < bp);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, [bp]);
    return isM;
};

const useIsPortrait = () => {
    const [isPortrait, setIsPortrait] = useState(
        typeof window !== 'undefined'
            ? window.matchMedia('(orientation: portrait)').matches
            : true
    );

    useEffect(() => {
        const mq = window.matchMedia('(orientation: portrait)');
        const h = (e: MediaQueryListEvent) => setIsPortrait(e.matches);
        mq.addEventListener ? mq.addEventListener('change', h) : mq.addListener(h);
        return () =>
            mq.removeEventListener ? mq.removeEventListener('change', h) : mq.removeListener(h);
    }, []);

    return isPortrait;
};

const BUTTON_AREA = 160;

/* ------------------------------------------------------------------ */
/* Komponente                                                         */
/* ------------------------------------------------------------------ */
const ModuleStep6: React.FC = () => {
    const isMobile   = useIsMobile();
    const isPortrait = useIsPortrait();
    const { formData } = useForm();

    /* ---------- Refs & States ---------- */
    const sigPad = useRef<SignaturePad>(null);
    const [savedSignature, setSavedSignature] = useState<string | null>(null);

    const [canvasWidth, setCanvasWidth] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);

    const [overlayDims, setOverlayDims] = useState({ w: 0, h: 0 });
    const [showOverlay, setShowOverlay] = useState(false);

    const [mailSendTo, setMailSendTo] = useState('');
    const [mailCCSendTo, setMailCCSendTo] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');

    const [notificationMessage, setNotificationMessage] = useState('');
    const [jobStatus, setJobStatus] = useState<string | null>(null);



    /* ------------------------------------------------------------------ */
    /* Data-Loading                                                       */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        if (!formData.jobID) return;
        axios.get(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`)
            .then(r => {
                const j = r.data;
                // CustomerSignature kommt jetzt als Storage-PFAD (jobs/<id>/CustomerSignature-<suffix>.png)
                const sigPath = j.CustomerSignature || null;
                setSavedSignature(
                    sigPath ? `${config.host}/get_image/${encodePath(sigPath)}` : null
                );
                setMailSendTo(j.MailSendTo || '');
                setMailCCSendTo(j.MailCCSendTo || '');
                setJobStatus(j.Status || null);
            });
    }, [formData.jobID]);

    useEffect(() => {
        if (!formData.customerID) return;
        axios.get(`${config.host}/customers/${encodeURIComponent(formData.customerID)}`)
            .then(r => setCustomerEmail(r.data.Email || ''));
    }, [formData.customerID]);

    /* ------------------------------------------------------------------ */
    /* Desktop-Canvas-Breite                                              */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        const upd = () => {
            if (containerRef.current) setCanvasWidth(containerRef.current.offsetWidth);
        };
        upd();
        window.addEventListener('resize', upd);
        return () => window.removeEventListener('resize', upd);
    }, []);

    /* ------------------------------------------------------------------ */
    /* Laden der Signatur ins Pad                                         */
    /* ------------------------------------------------------------------ */
    const repaintSignature = useCallback(() => {
    if (!sigPad.current) return;

    // kleinen Delay, damit die Canvas nach Resize fertig ist
    const id = setTimeout(() => {
        try {
        if (savedSignature) {
            // Breite/Höhe explizit mitgeben, dann wird korrekt gescaled
            const opts: any = {};
            if (!showOverlay) {
            // Desktop‑Pad
            opts.width  = canvasWidth || undefined;
            opts.height = 200;
            } else {
            // Overlay‑Pad
            opts.width  = overlayDims.w || undefined;
            opts.height = overlayDims.h || undefined;
            }
            sigPad.current!.fromDataURL(savedSignature, opts);
        } else {
            sigPad.current!.clear();
        }
        } catch (e) {
        // failsafe: falls URL noch nicht geladen o.ä.
        // kein throw – einfach ignorieren
        }
    }, 0);

    return () => clearTimeout(id);
    }, [savedSignature, canvasWidth, overlayDims.w, overlayDims.h, showOverlay]);

    useEffect(() => {
    const cleanup = repaintSignature();
    return cleanup;
    }, [repaintSignature]);


    /* ------------------------------------------------------------------ */
    /* Overlay-Größe                                                      */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        if (!showOverlay) return;
        const update = () =>
            setOverlayDims({
                w: window.innerWidth,
                h: window.innerHeight - BUTTON_AREA
            });
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [showOverlay]);


    /* ------------------------------------------------------------------ */
    /* Helper: ist das Pad leer?                                          */
    /* ------------------------------------------------------------------ */
    const padIsEmpty = () =>
        !(sigPad.current as any) || (sigPad.current as any).isEmpty?.();

    const hasSignature = !!savedSignature || !padIsEmpty();

    /* ------------------------------------------------------------------ */
    /* Signature-Aktionen                                                 */
    /* ------------------------------------------------------------------ */
    const clearSignature = async () => {
    if (!hasSignature) return;

    // für Rollback im Fehlerfall
    const prev = savedSignature;

    try {
        setSavedSignature(null);
        sigPad.current?.clear();

        if (!formData.jobID) return;

        await axios.put(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`, {
        CustomerSignature: null
        });
        await axios.delete(`${config.host}/signature/${encodeURIComponent(formData.jobID)}`);

        toast('Unterschrift gelöscht');
    } catch (err) {
        // Rollback
        setSavedSignature(prev || null);
        repaintSignature?.();
        toast('Fehler beim Löschen der Unterschrift');
        console.error(err);
    }
    };

    const saveSignature = async () => {
    if (!sigPad.current) return;

    try {
        // 1) Daten-URL erzeugen
        let dataURL: string;
        if (isMobile && isPortrait) {
        dataURL = (sigPad.current as any).getTrimmedCanvas().toDataURL('image/png');
        } else {
        const orig = sigPad.current.getCanvas();
        const tmp  = document.createElement('canvas');
        tmp.width  = orig.width;
        tmp.height = orig.height;
        const ctx  = tmp.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.fillStyle = '#FFF';
        ctx.fillRect(0, 0, tmp.width, tmp.height);
        ctx.drawImage(orig, 0, 0);
        dataURL = tmp.toDataURL('image/png');
        }

        // 2) Optimistische Vorschau
        setSavedSignature(dataURL);

        // 3) Upload
        const resp = await axios.post(
        `${config.host}/upload-signature/${encodeURIComponent(formData.jobID)}`,
        { signature: dataURL }
        );

        // 4) finale URL setzen (Pfad bevorzugt)
        if (resp?.data?.path) {
        const path = resp.data.path as string;
        setSavedSignature(`${config.host}/get_image/${encodePath(path)}`);
        } else if (resp?.data?.url) {
        setSavedSignature(resp.data.url as string);
        }

        setShowOverlay(false);
        repaintSignature?.();
        toast('Unterschrift gespeichert');
    } catch (err) {
        toast('Fehler beim Speichern der Unterschrift');
        console.error(err);
    }
    };


    /* ------------------------------------------------------------------ */
    /* Draft / Submit + Mail-Save                                         */
    /* ------------------------------------------------------------------ */
    const saveDraft = async () => {
        await axios.put(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`, {
            Status: 'draft'
        });
        setNotificationMessage('Entwurf erfolgreich gespeichert');
        setJobStatus('draft');
        setTimeout(() => setNotificationMessage(''), 3000);
    };

    const submitJob = async () => {
        await axios.put(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`, {
            Status: 'final'
        });
        setNotificationMessage('Bautagebuch eingereicht');
        setJobStatus('final');
        setTimeout(() => setNotificationMessage(''), 3000);
    };

    const saveEmailAddresses = async () => {
        await axios.put(`${config.host}/jobs/${encodeURIComponent(formData.jobID)}`, {
            MailSendTo: mailSendTo,
            MailCCSendTo: mailCCSendTo
        });
    };

    /* ------------------------------------------------------------------ */
    /* Status-Mapping                                                     */
    /* ------------------------------------------------------------------ */
    const mappedStatus =
        !jobStatus ? 'Noch nicht gespeichert'
        : jobStatus === 'draft' ? 'Als Entwurf gespeichert'
        : jobStatus === 'final' ? 'Bautagebuch eingereicht'
        : jobStatus === 'generated' ? 'Bautagebuch eingereicht und PDF erstellt'
        : jobStatus;

    // Snackbar / Notification helper
    const toast = React.useCallback((msg: string, ms = 3000) => {
    setNotificationMessage(msg);
    window.setTimeout(() => setNotificationMessage(''), ms);
    }, []);

    /* ------------------------------------------------------------------ */
    /* Render                                                             */
    /* ------------------------------------------------------------------ */
    return (
        <div style={{ padding: 20 }} ref={containerRef}>
            {/* Notification */}
            {notificationMessage && (
                <NotificationGroup style={{
                    position: 'fixed',
                    top: 30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000
                }}>
                    <Notification type={{ style: 'success', icon: true }}>
                        <span>{notificationMessage}</span>
                    </Notification>
                </NotificationGroup>
            )}

            <Card orientation="vertical">
                <CardHeader className="k-hbox custom-card-header">
                    <GetUserAvatar />
                    <div>
                        <CardTitle>Step 6: Unterschrift</CardTitle>
                        <CardSubtitle>Bitte unterschreiben Sie hier</CardSubtitle>
                    </div>
                </CardHeader>

                <CardBody>
                    {/* ---------- DESKTOP ---------- */}
                    {!isMobile && (
                        <>
                            <label>Unterschrift</label>
                            <div style={{ border: '1px solid #ccc', marginBottom: 20 }}>
                                <SignaturePad
                                key={`${savedSignature ?? 'empty'}|${canvasWidth}x200`}
                                ref={sigPad}
                                canvasProps={{ width: canvasWidth, height: 200 }}
                                />
                            </div>
                            <StyledButton
                                themeColor="primary"
                                onClick={saveSignature}
                                style={{ width: '100%', marginBottom: 20 }}
                            >
                                Unterschrift speichern
                            </StyledButton>
                            <StyledButton
                                themeColor="base"
                                onClick={clearSignature}
                                disabled={!hasSignature}
                                style={{ width: '100%', marginBottom: 10 }}
                            >
                                Unterschrift löschen
                            </StyledButton>
                        </>
                    )}

                    {/* ---------- MOBILE – Voransicht ---------- */}
                    {isMobile && !showOverlay && (
                        <>
                            <label>Unterschrift</label>
                            <div style={{
                                position: 'relative',
                                border: '1px solid #ccc',
                                marginBottom: 20,
                                height: 120,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {savedSignature
                                    ? <img src={savedSignature} alt="Signatur" loading="lazy"
                                        style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                    : <span style={{ color: '#888' }}>Keine Unterschrift vorhanden</span>}
                                {isPortrait && (
                                    <div style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '100%',
                                        background: 'rgba(255,255,255,0.8)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        padding: 10
                                    }}>
                                        Bitte&nbsp;drehen&nbsp;Sie<br />den&nbsp;Bildschirm
                                    </div>
                                )}
                            </div>
                            <StyledButton
                                themeColor="primary"
                                onClick={() => setShowOverlay(true)}
                                style={{ width: '100%', marginBottom: 20 }}
                            >
                                Unterschreiben
                            </StyledButton>
                        </>
                    )}

                    {/* Sende Kopie des BTBs direkt an Kunden und das BKA und den Papst

                    <div style={{ marginBottom: 20 }}>
                        <label>E-Mail Adresse des Kunden</label>
                        <Input
                            value={mailSendTo || customerEmail}
                            onChange={e => setMailSendTo(e.value)}
                            onBlur={saveEmailAddresses}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ marginBottom: 20 }}>
                        <label>Kopie an</label>
                        <Input
                            value={mailCCSendTo}
                            onChange={e => setMailCCSendTo(e.value)}
                            onBlur={saveEmailAddresses}
                            style={{ width: '100%' }}
                        />
                    </div>

                    */}

                    <div style={{ marginBottom: 20 }}>
                        <label>Status</label>
                        <Input value={mappedStatus} readOnly style={{ width: '100%' }} />
                    </div>

                    <StyledButton
                        themeColor="primary"
                        onClick={saveDraft}
                        style={{ width: '100%', marginBottom: 20 }}
                    >
                        Entwurf speichern
                    </StyledButton>
                    <StyledButton
                        themeColor="primary"
                        onClick={submitJob}
                        style={{ width: '100%' }}
                    >
                        Einreichen
                    </StyledButton>
                </CardBody>
            </Card>

            {/* ---------- FULLSCREEN OVERLAY ---------- */}
            {showOverlay && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: '#fff',
                    zIndex: 2000,
                    display: 'flex',
                    flexDirection: 'column'
                }}>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        <SignaturePad
                        key={`${savedSignature ?? 'empty'}|${overlayDims.w}x${overlayDims.h}`}
                        ref={sigPad}
                        canvasProps={{
                            width: overlayDims.w,
                            height: overlayDims.h,
                            style: {
                            background: '#fff',
                            touchAction: 'none',
                            border: '1px dashed #666'
                            }
                        }}
                        />
                    </div>

                    <div style={{
                        padding: '30px 10px',
                        flex: `0 0 ${BUTTON_AREA}px`
                    }}>
                        <StyledButton
                            themeColor="primary"
                            onClick={saveSignature}
                            style={{ width: '100%', marginBottom: 10 }}
                        >
                            Unterschrift speichern
                        </StyledButton>
                        <StyledButton
                            themeColor="base"
                            onClick={clearSignature}
                            disabled={!hasSignature}
                            style={{ width: '100%', marginBottom: 10 }}
                        >
                            Unterschrift löschen
                        </StyledButton>
                        <StyledButton
                            themeColor="base"
                            onClick={() => setShowOverlay(false)}
                            style={{ width: '100%' }}
                        >
                            Abbrechen
                        </StyledButton>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModuleStep6;
